import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { IDocumentSearchRequest, IDocumentSearchRequestQuery } from './shared/models/document-search-request';
import { DocumentService } from './shared/services/document.service';
import { debounceTime, distinctUntilChanged, filter, Observable, of, Subscription, switchMap, take } from 'rxjs';
import { IDocumentSearchResult } from './shared/models/document-search-result';
import { AuthService } from '../../shared/services/auth.service';
import { IApiResponse } from '../../shared/models/api-response';
import { IUser } from '../../shared/models/user';
import { IOptionWithIcon, IOption } from '../controls/models/option';
import { SearchResultLayout } from './shared/constants/search-results-layout';
import { appConstants } from '../../shared/constants/app-constants';
import { materialSymbols } from '../../shared/constants/material-symbols';
import { IDocumentSearchFacet, IDocumentSearchFacetItem } from './shared/models/document-search-facet';
import { UserUtility } from '../../shared/utilities/user-utility';
import { dropdownFormControlNames, facetTypes, saLibraryPublicationCommitteeType } from '../../shared/constants/sa-library-constants';
import { faSearch, faX } from '@fortawesome/free-solid-svg-icons';
import  * as dateUtils  from '../../shared/utilities/date-utility';

@Component({
    selector: 'app-sa-library',
    templateUrl: './sa-library.component.html',
    styleUrl: './sa-library.component.scss',
    standalone: false
})
export class SaLibraryComponent implements OnInit, OnDestroy {

  searchIcon: string = materialSymbols.search;
  searchResults: IDocumentSearchResult | null = null;
  facetCommittees: IDocumentSearchFacetItem[] = [];
  facetPublicationTypes: IDocumentSearchFacetItem[] = [];
  facetPublicationStatuses: IDocumentSearchFacetItem[] = [];
  subscriptions: Subscription = new Subscription();
  loading: boolean = true;
  currentUser: IUser | null = null;
  isNomOrgOnly: boolean = false;
  myCommitteeCodes: string[] = [];
  committeeCtrl = dropdownFormControlNames.committeeCtrl;
  publicationTypeDropdownCtrl = dropdownFormControlNames.publicationTypeDropdownCtrl;
  publicationStatusDropdownCtrl = dropdownFormControlNames.publicationStatusDropdownCtrl;
  facetCommittee = facetTypes.committee;
  facetPublicationType = facetTypes.publicationType;
  facetPublicationStatus = facetTypes.publicationStatus;
  allPublications = saLibraryPublicationCommitteeType.allPublications;
  myCommittee = saLibraryPublicationCommitteeType.myCommittee;
  isDefaultPublication: boolean = false;
  faX = faX;

  facetCommitteesOptionValues: IOption<string>[] = [];
  facetPublicationTypesOptionValues: IOption<string>[] = [];
  facetPublicationStatusesOptionValues: IOption<string>[] = [];

  searchByItems: IOption<string>[] = [{label: "Title", value: "title"},
                                      {label: "Designation", value: "designation"},
                                      {label: "Committee", value: "committee"}];

  gridLayout: IOptionWithIcon<string> =
  {
    value: 'grid',
    label: 'Grid',
    iconName: 'grid_view',
    name: 'grid',
  };

  listLayout: IOptionWithIcon<string> =
  {
    value: 'list',
    label: 'List',
    iconName: 'list',
    name: 'list',
  };

  allDropdownItem: IDocumentSearchFacetItem =
  {
    label: 'All',
    count: 0,
    isChecked: false
  };

  layoutOptions: IOptionWithIcon<string>[] = [
    this.gridLayout,
    this.listLayout
  ];

  layoutControl: FormControl= new FormControl([this.gridLayout.value]);  // Default to grid view
  selectedLayout: SearchResultLayout = SearchResultLayout.Grid;


  sortDateAsc: IOption<string> = { value: 'dateAsc', label: 'Date Ascending' };
  sortDateDesc: IOption<string> = { value: 'dateDesc', label: 'Date Descending' };
  sortRelevance: IOption<string> = { value: 'relevance', label: 'Relevance' };
  sortDesignation: IOption<string> = { value: 'designation', label: 'Designation' };

  sortOptions: IOption<string>[] = [
    this.sortDateAsc,
    this.sortDateDesc,
    this.sortRelevance,
    this.sortDesignation
  ];

  searchForm = new FormGroup({
    searchTermCtrl: new FormControl(),
    searchByCtrl: new FormControl(),
    startDateCtrl: new FormControl(),
    endDateCtrl: new FormControl(),
    sortByCtrl: new FormControl(),
    publicationCommitteeTypeCtrl: new FormControl(this.myCommittee), // Default to 'My Committee's Publications'
    committeeCtrl: new FormControl('All'),
    publicationTypeDropdownCtrl: new FormControl('All'),
    publicationStatusDropdownCtrl: new FormControl('All')
  });


  minEndDate: Date = new Date();
  maxStartDate: Date = new Date();

  constructor(
    private _authService: AuthService,
    private _documentService: DocumentService
  ) { }


  ngOnInit(): void {
    this.initializeOnLayoutChange();
    this.initializeMyCommitteeCodes();
    this.initializeOnSearchFilterChange();
    this.initialize();
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  initializePublicationType(){
    if(this.currentUser){
      this.isNomOrgOnly = UserUtility.isNomOrgOnly(this.currentUser);
      const isSRTorGMO = UserUtility.isSRTorGMO(this.currentUser);
      if(this.isNomOrgOnly || isSRTorGMO) {
        this.isDefaultPublication = true;
        this.searchForm.get('publicationCommitteeTypeCtrl')?.setValue(this.allPublications);
      }
    }
  }

  initialize() {
    if(this.currentUser){
      this.initializeDefaultFilters();
    }
  }

  initializeDefaultFilters() {
    const savedFilters = localStorage.getItem(appConstants.localStorageKey.saLibrarySearch);
    if (savedFilters) {
      const filters = JSON.parse(savedFilters);
      this.searchForm.patchValue(filters);
    }
    else {
      // Construct default empty filter.
      this.searchForm.patchValue({ sortByCtrl: this.sortDateDesc.value });
    }
  }

  initializeMyCommitteeCodes() {
    this.myCommitteeCodes = [];

    this.subscriptions.add(this._authService.currentUserSubject
      .pipe(
        filter(user => !!user),
        take(1)
      )
      .subscribe(user => {
      if (user) {
        this.currentUser = user;

        let generateMyCommitteeCodes: string[] = [];

        UserUtility.getCommitteeCodes(user).forEach(element => {
          let code = element.trim();
          generateMyCommitteeCodes.push(code);

          if(code.length > 6) {
            generateMyCommitteeCodes.push(code.substring(0, 6));
          }

        });
        this.myCommitteeCodes = generateMyCommitteeCodes;
        this.initializePublicationType();
      }
    }));
  }

  initializeOnLayoutChange() {
    this.subscriptions.add(this.layoutControl.valueChanges.subscribe({
      next: (layout: string[] | null) => {
        if(layout && layout.length > 0) {
          this.selectedLayout = layout[0] === this.gridLayout.value ? SearchResultLayout.Grid : SearchResultLayout.List;
        }
      }
    }));
  }

  initializeOnSearchFilterChange() {
    let searchDocuments$ = this.searchForm.valueChanges.pipe(
      debounceTime(800),
      distinctUntilChanged((prev, curr) : boolean => {
        const result =
          prev?.searchTermCtrl === curr?.searchTermCtrl
          && prev?.searchByCtrl === curr?.searchByCtrl
          && prev?.startDateCtrl === curr?.startDateCtrl
          && prev?.endDateCtrl === curr?.endDateCtrl
          && prev?.sortByCtrl?.toLocaleLowerCase() === curr?.sortByCtrl?.toLocaleLowerCase()
          && prev?.publicationCommitteeTypeCtrl === curr?.publicationCommitteeTypeCtrl
          && prev?.committeeCtrl === curr?.committeeCtrl
          && prev?.publicationTypeDropdownCtrl === curr?.publicationTypeDropdownCtrl
          && prev?.publicationStatusDropdownCtrl === curr?.publicationStatusDropdownCtrl;
        return result;
      }),
      switchMap((formData) => {
        this.loading = true;
        return this.searchDocuments(formData, 1);
      })
    );

    this.subscribeSearchResults(searchDocuments$);
  }

  searchDocuments(formData: Partial<ISearchFormData>, page: number): Observable<IApiResponse<IDocumentSearchResult> | null> {
    const searchRequest = this.buildSearchRequest(formData, page);
    if (searchRequest) {
      return this._documentService.documentSearch(searchRequest);
    } else {
      return of<IApiResponse<IDocumentSearchResult> | null>(null);
    }
  }

  buildSearchRequest(formData: Partial<ISearchFormData>, page: number) : IDocumentSearchRequest | null{
    if(!formData || !this.currentUser) {
      return null;
    }

    if(formData.startDateCtrl != '' && formData.endDateCtrl != '')
      this.updateMinMaxDate(formData.startDateCtrl, formData.endDateCtrl);

    const searchRequest: IDocumentSearchRequest = {
      pageNumber: page,
      pageSize: 50,
      sort: formData.sortByCtrl ? formData.sortByCtrl : this.sortDateDesc.value,
      query: this.generateQuery(formData),
      fromDate: formData.startDateCtrl ? dateUtils.toString(formData.startDateCtrl) : '',
      toDate: formData.endDateCtrl ? dateUtils.toString(formData.endDateCtrl) : '',
      searchTerm: formData.searchTermCtrl ? formData.searchTermCtrl : '',
      searchBy: formData.searchByCtrl ? formData.searchByCtrl : 'all',
      download: 'no',
    };
    localStorage.setItem(appConstants.localStorageKey.saLibrarySearch, JSON.stringify(formData));

    return searchRequest;
  }

  updateMinMaxDate(currentStartDate: Date, currentEndDate: Date) {
    this.minEndDate = currentStartDate;
    this.maxStartDate = currentEndDate;
    if(currentStartDate != null && currentEndDate != null && currentEndDate < this.minEndDate) {
      this.searchForm.get('endDateCtrl')?.setValue(this.minEndDate);
    }
    if(currentStartDate != null && currentEndDate != null  && currentEndDate > this.maxStartDate) {
      this.searchForm.get('startDateCtrl')?.setValue(this.maxStartDate);
    }
  }

  subscribeSearchResults(searchDocuments$: Observable<IApiResponse<IDocumentSearchResult> | null> ): void {
    const searchResultSubscription = searchDocuments$.subscribe({
      next: (apiResponse) => {
        if (apiResponse && apiResponse?.success) {
          this.searchResults = apiResponse.content;
          if(this.searchResults && this.searchResults.facets) {
            this.setFacetDropdowns(this.searchResults.facets);
          }
        }
        else {
          this.searchResults = null;
        }

        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
      }
    });
    this.subscriptions.add(searchResultSubscription);
  }

  setFacetDropdowns(facets: IDocumentSearchFacet[]) {
    this.facetCommittees = facets.filter(facet => facet.label === 'Committee')?.[0]?.items;
    this.facetPublicationTypes = facets.filter(facet => facet.label === 'Publication Type')?.[0]?.items;
    this.facetPublicationStatuses = facets.filter(facet => facet.label === 'Publication Status')?.[0]?.items;
    this.updateDropdownValues();
    this.setDropdownOptionValues();
  }

  updateDropdownValues(){
    this.searchForm.get(this.committeeCtrl)?.valueChanges.subscribe(value => {
      if (value !== 'All') {
        this.facetCommittees = this.facetCommittees.filter(committee => committee.label !== 'All');
      }
    });

    this.searchForm.get(this.publicationTypeDropdownCtrl)?.valueChanges.subscribe(value => {
      if (value !== 'All') {
        this.facetPublicationTypes = this.facetPublicationTypes.filter(type => type.label !== 'All');
      }
    });

    this.searchForm.get(this.publicationStatusDropdownCtrl)?.valueChanges.subscribe(value => {
      if (value !== 'All') {
        this.facetPublicationStatuses = this.facetPublicationStatuses.filter(status => status.label !== 'All');
      }
    });
  }


  setDropdownOptionValues(){
    this.facetCommitteesOptionValues = this.facetCommittees.map(item => ({
      label: item.label + ` (${item.count})` || '',
      value: item.label || ''
    }));
    this.facetPublicationTypesOptionValues = this.facetPublicationTypes.map(item => ({
      label: item.label + ` (${item.count})` || '',
      value: item.label || ''
    }));

    this.facetPublicationStatusesOptionValues = this.facetPublicationStatuses.map(item => ({
      label: item.label + ` (${item.count})` || '',
      value: item.label || ''
    }));

  }
  onPageChange(page: number): void {
     if (this.searchForm.valid) {
       const searchDocuments$ = this.searchDocuments(this.searchForm.value, page);
       this.subscribeSearchResults(searchDocuments$);
     }
  }

  onRadioButtonChange(selectedValue: string): void {
    this.clearAllFilters(true);
  }

  generateQuery(formData: Partial<ISearchFormData>): IDocumentSearchRequestQuery {
    let query: IDocumentSearchRequestQuery;
    let committeeList: string[] = [];
    let publicationTypeList: string[] = [];
    let publicationStatusList: string[] = [];

    if(formData.committeeCtrl && formData.committeeCtrl != 'All' && formData.committeeCtrl.trim() != '') {
      const selectedCommitteeCode = this.extractText(formData.committeeCtrl);
      committeeList.push(selectedCommitteeCode);
    } else if(formData.publicationCommitteeTypeCtrl == this.myCommittee) {
      committeeList = [...this.myCommitteeCodes]
    }

    if(formData.publicationTypeDropdownCtrl && formData.publicationTypeDropdownCtrl != 'All' && formData.publicationTypeDropdownCtrl.trim() != '') {
      const selectedPublicationType = this.extractText(formData.publicationTypeDropdownCtrl);
      publicationTypeList.push(selectedPublicationType);
    }

    if(formData.publicationStatusDropdownCtrl && formData.publicationTypeDropdownCtrl != 'All' && formData.publicationStatusDropdownCtrl.trim() != '') {
      const selectedPublicationStatus = this.extractText(formData.publicationStatusDropdownCtrl);
      publicationStatusList.push(selectedPublicationStatus);
    }

    query = {
      committee: committeeList,
      publicationType: publicationTypeList,
      publicationStatus: publicationStatusList,
      sdo: []
    };

    return query;
  }

  extractText = (value: string) => {
    return value.split('(')[0].trim();
  };

  clearAllFilters = (radioChanges: boolean) => {
    localStorage.removeItem(appConstants.localStorageKey.saLibrarySearch);
    this.resetSearchDetails(radioChanges);
  }

  resetSearchDetails  = (radioChanges: boolean) => {
    let isSelectedAllPublications = true;
    if(radioChanges){
      const publicationTypeCtrl = this.searchForm?.get('publicationCommitteeTypeCtrl')?.value;
      isSelectedAllPublications = publicationTypeCtrl == this.allPublications;
    } else {
      if(!this.isNomOrgOnly && !this.isDefaultPublication){
        isSelectedAllPublications = false;
      }
    }

    this.searchForm.patchValue({
      searchTermCtrl: '',
      searchByCtrl: '',
      startDateCtrl: null,
      endDateCtrl: null,
      sortByCtrl: this.sortDateDesc.value,
      publicationCommitteeTypeCtrl: isSelectedAllPublications ? this.allPublications : this.myCommittee,
      committeeCtrl: '',
      publicationTypeDropdownCtrl: '',
      publicationStatusDropdownCtrl: ''
    });
  }

}

interface ISearchFormData {
  searchTermCtrl: string | null;
  searchByCtrl: string | null;
  startDateCtrl: any;
  endDateCtrl: any;
  sortByCtrl: string | null;
  publicationCommitteeTypeCtrl: string | null;
  committeeCtrl : string | null;
  publicationTypeDropdownCtrl: string | null;
  publicationStatusDropdownCtrl: string | null;
}
